/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RoleListComponent - Fully Responsive with Clear Search Button
 * ------------------
 * Displays all application roles in a fully themed, responsive layout.
 * 
 * Desktop (>991px): Traditional table with sticky actions column
 * Mobile (≤991px): Stacked card layout with vertically arranged data
 *
 * NEW FEATURE: Search Clear Button
 * • X button appears at right end of search input when search term exists
 * • Clears search input and triggers search reset when clicked
 * • Disappears when input is empty
 * • Responsive positioning (absolute positioned within input container)
 *
 * Features:
 * • Dual-layout: Table (desktop) + Cards (mobile) with seamless switching
 * • Server-driven search with Clear (X) button + explicit "Search" trigger
 * • Sticky Actions column (desktop only) for consistent edit/delete interactions
 * • Responsive design using CSS media queries + React state detection
 * • Smooth pagination via reusable Paginator component
 * • Theme-aware colors, shadows, spacing, and animations
 *
 * Props:
 * - roles: Array of role objects (id, name, description, permissions)
 * - page: Current page number
 * - pageCount: Total number of pages
 * - search: Current search text
 * - onSearch: Callback to trigger backend search
 * - onCreate: Handler → Create new role
 * - onEdit: Handler → Edit selected role
 * - onDelete: Handler → Delete selected role
 * - onPage: Pagination handler for previous/next/page navigation
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import ThemedInput from '../common/ThemedInput';
import Paginator from '../common/Paginator';
import Button from '../Button';

function RoleListComponent({
    appName,
    roles = [],
    page = 1,
    pageCount = 1,
    search = '',
    onSearch = () => { },
    onCreate,
    onEdit,
    onDelete,
    onPage
}) {
    const { theme } = useTheme();

    // -- Responsive state: Track screen size for layout switching --
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

    useEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth <= 991);
        }
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // -- Search state management --
    const [searchInput, setSearchInput] = useState(search || '');
    const searchRef = useRef(null);

    useEffect(() => {
        setSearchInput(search || '');
    }, [search]);

    // -- Check if search is active (for Clear button visibility) --
    const hasSearchTerm = (searchInput || '').trim().length > 0;

    // -- Search handlers --
    const handleSearchChange = useCallback((e) => {
        setSearchInput(e.target.value);
    }, []);

    const triggerSearch = useCallback(() => {
        const term = (searchInput || '').trim();
        onSearch(term);
    }, [searchInput, onSearch]);

    // -- NEW: Clear search functionality --
    const clearSearch = useCallback(() => {
        setSearchInput('');
        onSearch(''); // Reset backend search
        if (searchRef.current) {
            searchRef.current.focus(); // Return focus to input for better UX
        }
    }, [onSearch]);

    const handleSearchKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            triggerSearch();
        } else if (e.key === 'Escape' && hasSearchTerm) {
            clearSearch(); // ESC clears search
        }
    }, [triggerSearch, clearSearch, hasSearchTerm]);

    // -- Action handlers --
    const handleCreate = useCallback(() => {
        if (onCreate) onCreate();
    }, [onCreate]);

    const handleEdit = useCallback((role) => {
        if (onEdit) onEdit(role);
    }, [onEdit]);

    const handleDelete = useCallback((role) => {
        if (onDelete) onDelete(role);
    }, [onDelete]);

    // -- Base card styles (shared across layouts) --
    const cardStyle = {
        width: 'auto',
        maxWidth: '100%',
        background: '#fff',
        borderRadius: theme.cardRadius,
        boxShadow: '0 6px 32px 0 rgba(80,80,180,0.10)',
        padding: isMobile ? '10px' : '2em',
        margin: '32px auto',
        position: 'relative'
    };

    // -- Desktop table: Sticky actions column styles --
    const actionsColStyle = {
        position: 'sticky',
        right: 0,
        background: '#fff',
        zIndex: 2,
        boxShadow: '0 0 0 1px #eee'
    };

    // -- Mobile card styles --
    const mobileCardStyle = {
        background: '#fff',
        borderRadius: theme.cardRadius,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        padding: '1em',
        marginBottom: '1.5em',
        border: '1px solid #f0f0f0'
    };

    const mobileLabelStyle = {
        fontWeight: 600,
        color: theme.primary,
        fontSize: '14px',
        marginBottom: '4px'
    };

    const mobileValueStyle = {
        color: '#555',
        fontSize: '15px',
        paddingBottom: '12px',
        borderBottom: '1px solid #f5f5f5',
        marginBottom: '12px'
    };

    // -- NEW: Search container with relative positioning for Clear button --
    const searchContainerStyle = {
        position: 'relative',
        display: 'flex',
        width: '100%',
        flex: 1
    };

    const clearButtonStyle = {
        position: 'absolute',
        right: '12px', // Position left of Search button (12px padding + 30px button width)
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'rgba(255,255,255,0.9)',
        border: '1px solid #e0e0e0',
        borderRadius: '50%',
        width: '24px',
        height: '24px',
        display: hasSearchTerm ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        zIndex: 3,
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    };

    const clearIconStyle = {
        fontSize: '12px',
        color: '#666',
        lineHeight: 1
    };

    return (
        <div style={{
            minHeight: '80vh',
            background: '#f6f8fa',
            paddingTop: 5,
            paddingBottom: 40,
            paddingLeft: isMobile ? 0 : 15,
            paddingRight: isMobile ? 0 : 15
        }}>
            <div style={cardStyle}>
                {/* Header */}
                <h2
                    className="fw-bold mb-4"
                    style={{
                        color: theme.primary,
                        fontSize: isMobile ? '1.5em' : '2em'
                    }}
                >
                    Roles {appName ? `- ${appName}` : ""}
                </h2>

                {/* Search + Create Row - Responsive flex layout */}
                <div className="mb-3">
                    <div
                        className={`d-flex ${isMobile ? 'flex-column' : 'flex-row'} align-items-stretch`}
                        style={{ gap: isMobile ? 12 : 26 }}
                    >
                        {/* Search Section with Clear Button */}
                        <div className={isMobile ? "w-100 mb-2" : "flex-grow-1"}>
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                <div style={{ display: 'flex', width: '100%', position: 'relative' }}>
                                    {/* NEW: Search container with Clear button overlay */}
                                    <div style={searchContainerStyle}>
                                        <ThemedInput
                                            ref={searchRef}
                                            value={searchInput}
                                            onChange={handleSearchChange}
                                            onKeyDown={handleSearchKeyDown}
                                            placeholder="Search roles by Name, Label or Description..."
                                            style={{
                                                paddingRight: hasSearchTerm ? '70px' : '12px' // Extra padding for Clear button
                                            }}
                                            aria-label="Search roles"
                                        />

                                        {/* NEW: Clear (X) Button - Shows only when search term exists */}
                                        <button
                                            type="button"
                                            onClick={clearSearch}
                                            aria-label="Clear search"
                                            style={clearButtonStyle}
                                            title="Clear search (ESC)"
                                        >
                                            <i className="bi bi-x" style={clearIconStyle} />
                                        </button>
                                    </div>

                                    {/* Search Button */}
                                    <button
                                        type="button"
                                        onClick={triggerSearch}
                                        aria-label="Search"
                                        disabled={(searchInput || '').trim() === (search || '').trim()}
                                        style={{
                                            backgroundColor: theme.primary,
                                            borderRadius: theme.buttonRadius,
                                            padding: "8px 12px",
                                            cursor: (searchInput || '').trim() === (search || '').trim() ? 'not-allowed' : 'pointer',
                                            border: "none",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginLeft: '8px',
                                            opacity: (searchInput || '').trim() === (search || '').trim() ? 0.6 : 1
                                        }}
                                    >
                                        <i className="bi bi-search" style={{ color: "white", fontSize: '14px' }} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Create Button */}
                        <div className={isMobile ? "w-100" : ""}>
                            <Button
                                mode="primary-bold"
                                onClick={handleCreate}
                                style={{
                                    width: isMobile ? "100%" : "auto",
                                    marginTop: isMobile ? 14 : 0
                                }}
                            >
                                + Create Role
                            </Button>
                        </div>
                    </div>
                </div>

                {/* DESKTOP LAYOUT: Traditional Table (>991px) */}
                {!isMobile && (
                    <div className="table-responsive" style={{ overflowX: "auto" }}>
                        <table style={{
                            width: "100%",
                            minWidth: "1200px",
                            borderCollapse: "collapse",
                            marginBottom: 0
                        }}>
                            <thead>
                                <tr style={{
                                    background: theme.primary + '08',
                                    color: theme.primary,
                                    fontWeight: 600,
                                    fontSize: 16
                                }}>
                                    <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>
                                        Name
                                    </th>
                                    <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>
                                        Label
                                    </th>
                                    <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>
                                        Description
                                    </th>
                                    <th style={{ padding: "12px 11px", textAlign: 'left', borderBottom: `2px solid ${theme.primary}15` }}>
                                        Permissions
                                    </th>
                                    <th style={{
                                        ...actionsColStyle,
                                        minWidth: 110,
                                        textAlign: 'center',
                                        borderBottom: `2px solid ${theme.primary}15`
                                    }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{
                                            textAlign: "center",
                                            padding: "40px 20px",
                                            color: "#bbb",
                                            fontSize: '16px'
                                        }}>
                                            No roles found.
                                        </td>
                                    </tr>
                                ) : (
                                    roles.map((role) => (
                                        <tr key={role.id} style={{ fontSize: 15 }}>
                                            <td style={{ padding: "14px 10px", color: "#666" }}>
                                                {role.name}
                                            </td>
                                            <td style={{ padding: "14px 10px", fontWeight: 500 }}>
                                                {role.label}
                                            </td>
                                            <td style={{ padding: "14px 10px", color: "#666" }}>
                                                {role.description}
                                            </td>
                                            <td style={{ padding: "14px 10px", color: theme.primary }}>
                                                {(role.default_permissions || []).join(", ")}
                                            </td>
                                            <td style={{
                                                ...actionsColStyle,
                                                padding: "14px 8px",
                                                textAlign: "center"
                                            }}>
                                                <span
                                                    style={{
                                                        marginRight: 12,
                                                        cursor: "pointer",
                                                        color: theme.primary,
                                                        fontSize: '18px'
                                                    }}
                                                    title="Edit Role"
                                                    onClick={() => handleEdit(role)}
                                                    tabIndex={0}
                                                    aria-label="Edit Role"
                                                >
                                                    <i className="bi bi-pencil-square" />
                                                </span>
                                                <span
                                                    style={{
                                                        cursor: "pointer",
                                                        color: "#d53b3b",
                                                        fontSize: '18px'
                                                    }}
                                                    title="Delete Role"
                                                    onClick={() => handleDelete(role)}
                                                    tabIndex={0}
                                                    aria-label="Delete Role"
                                                >
                                                    <i className="bi bi-trash" />
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* MOBILE LAYOUT: Stacked Cards (≤991px) */}
                {isMobile && (
                    <div style={{ marginTop: '1em' }}>
                        {roles.length === 0 ? (
                            <div style={{
                                textAlign: "center",
                                padding: "60px 20px",
                                color: "#bbb",
                                fontSize: '16px'
                            }}>
                                No roles found.
                            </div>
                        ) : (
                            roles.map((role) => (
                                <div key={role.id} style={mobileCardStyle}>
                                    {/* Name - Prominent */}
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: theme.primary,
                                        marginBottom: '12px',
                                        paddingBottom: '8px',
                                        borderBottom: `2px solid ${theme.primary}10`
                                    }}>
                                        {role.name}
                                    </div>

                                    {/* Label */}
                                    <div>
                                        <div style={mobileLabelStyle}>Label</div>
                                        <div style={mobileValueStyle}>{role.label}</div>
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <div style={mobileLabelStyle}>Description</div>
                                        <div style={mobileValueStyle}>{role.description}</div>
                                    </div>

                                    {/* Permissions */}
                                    <div>
                                        <div style={mobileLabelStyle}>Permissions</div>
                                        <div style={{
                                            ...mobileValueStyle,
                                            color: theme.primary
                                        }}>
                                            {(role.default_permissions || []).join(", ")}
                                        </div>
                                    </div>

                                    {/* Actions - Right aligned */}
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                        paddingTop: '12px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        <button
                                            onClick={() => handleEdit(role)}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid ' + theme.primary,
                                                color: theme.primary,
                                                padding: '8px 16px',
                                                borderRadius: theme.buttonRadius,
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                            aria-label="Edit Role"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(role)}
                                            style={{
                                                background: 'transparent',
                                                border: '1px solid #d53b3b',
                                                color: '#d53b3b',
                                                padding: '8px 16px',
                                                borderRadius: theme.buttonRadius,
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }}
                                            aria-label="Delete Role"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Pagination - Always visible */}
                <Paginator
                    page={page}
                    pageCount={pageCount}
                    onPrevious={onPage}
                    onPage={onPage}
                    onNext={onPage}
                    maxPagesToShow={3}
                    style={{ marginTop: isMobile ? '2em' : '3em' }}
                />

                {/* Decorative bottom gradient */}
                <div
                    style={{
                        height: 8,
                        width: '100%',
                        borderBottomLeftRadius: theme.cardRadius,
                        borderBottomRightRadius: theme.cardRadius,
                        background: theme.gradientBackground,
                        position: 'absolute',
                        left: 0,
                        bottom: 0
                    }}
                />
            </div>
        </div>
    );
}

export default RoleListComponent;
